import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@web/lib/utils.ts";

export type ComboboxOption = {
  id: string;
  label: string;
  /** Optional secondary line (e.g. genre parent). */
  hint?: string;
};

/**
 * Multi-select searchable combobox — same interaction language as genre
 * assignment and CountryCombobox. Used by the library FilterBar.
 */
export function SearchableMultiCombobox({
  label,
  options,
  selectedIds,
  disabled,
  placeholder = "Search…",
  emptyText = "No matches",
  onChange,
}: {
  label: string;
  options: readonly ComboboxOption[];
  selectedIds: readonly string[];
  disabled?: boolean;
  placeholder?: string;
  emptyText?: string;
  onChange: (ids: string[]) => void;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.hint?.toLowerCase().includes(q) ?? false),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    setHighlight(0);
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function toggle(id: string) {
    if (selected.has(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  }

  const count = selectedIds.length;

  return (
    <div ref={rootRef} className="relative w-36 shrink-0">
      {open ? (
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={label}
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          className={cn(
            "h-8 w-full rounded-md border border-line bg-panel px-2 text-sm text-text",
            "placeholder:text-faint focus-visible:outline-none disabled:opacity-50",
          )}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
              setQuery("");
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlight((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlight((i) => Math.max(i - 1, 0));
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              const hit = filtered[highlight];
              if (hit) toggle(hit.id);
            }
          }}
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          aria-label={label}
          aria-expanded={false}
          className={cn(
            "inline-flex h-8 w-full items-center gap-1 rounded-md border border-line bg-panel px-2 text-sm",
            count > 0 ? "text-text" : "text-muted",
            "hover:border-muted disabled:opacity-50",
          )}
          onClick={() => {
            if (disabled) return;
            setQuery("");
            setOpen(true);
          }}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {count > 0 ? `${label} · ${count}` : label}
          </span>
          <ChevronDown className="size-3.5 shrink-0 text-faint" aria-hidden />
        </button>
      )}

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          aria-multiselectable
          className="absolute top-full left-0 z-30 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-line bg-panel-elevated py-1 shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-2 py-1.5 text-xs text-faint">{emptyText}</li>
          ) : (
            filtered.map((opt, i) => {
              const isOn = selected.has(opt.id);
              return (
                <li key={opt.id} role="option" aria-selected={isOn}>
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs",
                      i === highlight ? "bg-row-hover text-text" : "text-muted hover:bg-row-hover hover:text-text",
                    )}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => toggle(opt.id)}
                  >
                    <span
                      className={cn(
                        "flex size-3.5 shrink-0 items-center justify-center rounded-sm border border-line",
                        isOn && "border-accent bg-accent text-accent-contrast",
                      )}
                      aria-hidden
                    >
                      {isOn && <Check className="size-2.5" />}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
